import type { RouteObject } from 'react-router';
import { Layout } from './components/Layout';
import { RequireAuth } from './features/auth/RequireAuth';

/**
 * ONE route definition, used by BOTH the browser router and the prerenderer.
 *
 * If the two ever drift, prerendered HTML stops matching what the browser
 * renders at the same URL — Google sees one page, the user sees another, which
 * is the cloaking accusation nobody wants to defend. Sharing the array makes
 * that impossible.
 *
 * `lazy` is fine on both sides: React Router's createStaticHandler resolves it
 * during the static query, exactly as the browser router does.
 */
export const routes: RouteObject[] = [
  {
    element: <Layout />,
    children: [
      // Home landed in Phase 9 — "/" is the dark home page, no longer a redirect.
      { index: true, lazy: async () => ({ Component: (await import('./pages/Home')).default }) },
      { path: '/courses', lazy: async () => ({ Component: (await import('./pages/Catalog')).default }) },
      { path: '/course/:slug', lazy: async () => ({ Component: (await import('./pages/CourseDetail')).default }) },
      { path: '/store', lazy: async () => ({ Component: (await import('./pages/Store')).default }) },
      { path: '/leaderboard', lazy: async () => ({ Component: (await import('./pages/Leaderboard')).default }) },
      { path: '/affiliate', lazy: async () => ({ Component: (await import('./pages/Affiliate')).default }) },
      { path: '/centres', lazy: async () => ({ Component: (await import('./pages/Centres')).default }) },
      { path: '/blog', lazy: async () => ({ Component: (await import('./pages/Blog')).default }) },
      { path: '/blog/:slug', lazy: async () => ({ Component: (await import('./pages/BlogPost')).default }) },
      { path: '/resources', lazy: async () => ({ Component: (await import('./pages/Resources')).default }) },
      { path: '/instructors', lazy: async () => ({ Component: (await import('./pages/Instructors')).default }) },
      { path: '/instructors/:slug', lazy: async () => ({ Component: (await import('./pages/InstructorProfile')).default }) },
      { path: '/category/:slug', lazy: async () => ({ Component: (await import('./pages/Category')).default }) },
      { path: '/event/:slug', lazy: async () => ({ Component: (await import('./pages/Event')).default }) },
      /**
       * These two live under /page/ because the prototype rendered them into
       * the same #pageTitle/#pageBody view as the static pages (openTeachPage
       * :1758, openBusiness :1969) — but each body is a form, so neither can be
       * a StaticPage row. React Router ranks static segments above dynamic
       * ones, so they win over /page/:slug regardless of order here.
       */
      { path: '/page/become-an-instructor', lazy: async () => ({ Component: (await import('./pages/BecomeInstructor')).default }) },
      { path: '/page/for-business', lazy: async () => ({ Component: (await import('./pages/ForBusiness')).default }) },
      { path: '/page/:slug', lazy: async () => ({ Component: (await import('./pages/StaticPage')).default }) },
      { path: '/cv-builder', lazy: async () => ({ Component: (await import('./pages/CvBuilder')).default }) },
      { path: '/verify', lazy: async () => ({ Component: (await import('./pages/Verify')).default }) },
      { path: '/verify/:certId', lazy: async () => ({ Component: (await import('./pages/Verify')).default }) },
      {
        path: '/learn/:slug/:module/:lesson',
        lazy: async () => {
          const Player = (await import('./pages/Player')).default;
          return {
            Component: () => (
              <RequireAuth>
                <Player />
              </RequireAuth>
            ),
          };
        },
      },
      {
        path: '/dashboard',
        lazy: async () => {
          const Dashboard = (await import('./pages/Dashboard')).default;
          return {
            Component: () => (
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            ),
          };
        },
      },
      /**
       * Admin panel — staff only. RequireAuth handles the guest case; the page
       * itself refuses a logged-in student, and every section endpoint re-checks
       * the role server-side (spec Part 5). Both /admin and /admin/:section
       * resolve to the same shell, which defaults to overview.
       */
      {
        path: '/admin',
        lazy: async () => {
          const Admin = (await import('./pages/Admin')).default;
          return {
            Component: () => (
              <RequireAuth>
                <Admin />
              </RequireAuth>
            ),
          };
        },
      },
      {
        path: '/admin/:section',
        lazy: async () => {
          const Admin = (await import('./pages/Admin')).default;
          return {
            Component: () => (
              <RequireAuth>
                <Admin />
              </RequireAuth>
            ),
          };
        },
      },
      { path: '*', lazy: async () => ({ Component: (await import('./pages/NotBuilt')).default }) },
    ],
  },
];
